import React, { useRef, useState } from 'react';
import type { ReactElement, RefObject } from 'react';
import type { ElementPopoverPlacementVariant } from '@construct-kit/core';
import {
  Card,
  Button,
  ArrowDownMd,
  spacingHelper,
  ElementPopover,
  Inline,
  paletteHelper,
} from '@construct-kit/core';
import styled from 'styled-components';
import { StyledPillButton } from './filterStyles';

const StyledOverlay = styled(Card)`
  margin-top: ${spacingHelper('extraSmall')};
`;

const StyledInline = styled(Inline).attrs({
  justifyContent: 'space-between',
  grow: false,
  inset: 'medium',
})`
  border-top: solid 1px ${paletteHelper('borderSecondary')};
  margin-top: ${spacingHelper('extraLarge')};
`;

export interface FilterPillButtonProps {
  label: string;
  selected: boolean;
  children: ReactElement;
  hideOverlayFooter?: boolean;
  onFilterPillClose?: () => void;
  onFilterPillOpen: () => void;
  onReset?: () => void;
  handleSearchOnClick?: (onClose?: () => void) => void;
  popOverPlacement?: ElementPopoverPlacementVariant;
}

type OverlayFooterProps = {
  onReset?: () => void;
  handleSearchOnClick: (onClose?: () => void) => void;
};

type openPillArgs = {
  label: string;
  selected: boolean;
  onToggle: () => void;
  isOpen: boolean;
  ref: RefObject<HTMLButtonElement>;
};

export const OverlayFooter = ({
  onReset,
  handleSearchOnClick,
}: OverlayFooterProps): ReactElement => (
  <StyledInline>
    <Button variant="naked" sizeVariant="medium" onClick={onReset}>
      Reset
    </Button>
    <Button
      variant="primary"
      sizeVariant="medium"
      onClick={() => {
        handleSearchOnClick();
      }}
    >
      Search
    </Button>
  </StyledInline>
);

const openPill =
  ({ label, selected, onToggle, isOpen, ref }: openPillArgs) =>
    () =>
      (
        <StyledPillButton
          onClick={onToggle}
          iconRight={<ArrowDownMd />}
          selected={selected}
          ref={ref}
          isOpen={isOpen}
        >
          {label}
        </StyledPillButton>
      );

export const FilterPillButton = ({
  label,
  selected,
  children,
  hideOverlayFooter,
  onFilterPillClose,
  onReset,
  handleSearchOnClick,
  popOverPlacement = 'bottom',
  onFilterPillOpen,
}: FilterPillButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const togglePill = () => {
    setIsOpen((prevState) => {
      if (prevState) {
        onFilterPillClose?.();
      } else {
        onFilterPillOpen();
      }

      return !prevState;
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    onFilterPillClose?.();
    triggerRef.current?.focus();
  };

  return (
    <ElementPopover
      onClick={() => {
        if (hideOverlayFooter && isOpen) handleClose();
      }}
      opened={isOpen}
      placement={popOverPlacement}
      onClickOutside={handleClose}
      trigger={openPill({ label, selected, onToggle: togglePill, isOpen, ref: triggerRef })}
      initialFocusRef={overlayRef}
      zIndex={98}
    >
      {() => (
        <StyledOverlay ref={overlayRef} tabIndex={-1}>
          {children}
          {!hideOverlayFooter && (
            <OverlayFooter
              onReset={onReset}
              handleSearchOnClick={() => {
                handleSearchOnClick?.(() => setIsOpen(false));
              }}
            />
          )}
        </StyledOverlay>
      )}
    </ElementPopover>
  );
};
